import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  FormController,
  MuiGrid,
  MuiModal,
  MuiTextField,
} from "../../../../components";
import { LANGUAGE_CODE, MAX_LENGTH_75 } from "../../../../helpers/constants";
import {
  localeFields,
  ManageLocaleKey,
} from "../../../../helpers/constants/siteCoreKeys";
import { isEmpty } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { userPreferredLanguageSelector } from "../../../../redux/slices/appState/appStateSelector";
import { KeyValueMap } from "../../../../redux/slices/appState/models";
import {
  LocalizedProgramTemplateNames,
  ManageLocales,
  NameMandatoryByLanguage,
} from "../../modelsAndSchemas/Models";
import { manageLocaleValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CancelFieldModal } from "../cancelFieldModal/CancelFieldModal";
import { ManageLocaleWrapper } from "./ManageLocale.style";
import { useGridContainer } from "../../../../hooks/useGridContainer";

interface ManageLocaleProps {
  pageId: string;
  show: boolean;
  getManageLocalsData: (data: any) => void; //any with type
  handleCloseModal: () => void;
  nameMandatoryByLanguage: NameMandatoryByLanguage | undefined;
  englishName: string;
  programNameByLanguage: string;
  localizedProgramTemplateNames: LocalizedProgramTemplateNames[];
  defaultManageLocalData: KeyValueMap;
  isEditTemplate: boolean;
  setIsManageLocalChange: (value: boolean) => void;
}

export const ManageLocale: FC<ManageLocaleProps> = ({
  pageId,
  show,
  getManageLocalsData,
  handleCloseModal,
  nameMandatoryByLanguage = { key: "" },
  programNameByLanguage,
  englishName,
  localizedProgramTemplateNames,
  defaultManageLocalData,
  isEditTemplate,
  setIsManageLocalChange,
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const { fetchComponentTitle, commonLabels, commonValidationMessages } =
    useSiteCore();
  const { fetchLanguageDropdownValues, commonARIALabel } =
    useNavigationSiteCore();

  // set key value pare data and sort mandatory to first
  const mergedLanguages = useMemo(() => {
    const languages = fetchLanguageDropdownValues();
    const fields = localeFields(commonLabels);
    return languages
      .map((language) => {
        const matchingPlaceholder = fields.find(
          (placeholder) => placeholder.key === language.value,
        );
        return {
          ...language,
          ...matchingPlaceholder,
          isMandatory: userSelectedLanguage === language.value,
          isEnglish:
            language.value === LANGUAGE_CODE.EN_US &&
            userSelectedLanguage !== LANGUAGE_CODE.EN_GB,
        };
      })
      .sort((a, b) => (b.isEnglish ? 1 : 0) - (a.isEnglish ? 1 : 0))
      .sort((a, b) => (b.isMandatory ? 1 : 0) - (a.isMandatory ? 1 : 0));
  }, [fetchLanguageDropdownValues, localeFields, userSelectedLanguage]);

  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { isDirty },
  } = useForm({
    mode: "all",
    resolver: yupResolver(
      manageLocaleValidationSchema(
        mergedLanguages as ManageLocales[],
        commonValidationMessages,
      ),
    ),
  });

  const onConfirmClick = () => {
    getManageLocalsData(getValues());
    handleCloseModal();
  };

  useEffect(() => {
    //first set data from api
    localizedProgramTemplateNames.forEach((data) =>
      setValue(data.localekey, data.name),
    );
    //set data if any field change
    Object.entries(defaultManageLocalData).forEach(([key, value]) => {
      // Check if value is not undefined, null, or an empty string
      if (value) setValue(key, value);
    });
    //find the current mandatory language and set the value
    const fields = localeFields(commonLabels).find(
      (data) => data.key === nameMandatoryByLanguage.key,
    );

    if (fields?.key) setValue(fields.key, programNameByLanguage);

    const isEnUsMissing = isLanguageMissing(
      localizedProgramTemplateNames,
      LANGUAGE_CODE.EN_US,
    );
    const isEnGbMissing = isLanguageMissing(
      localizedProgramTemplateNames,
      LANGUAGE_CODE.EN_GB,
    );
    const isDefaultDataMissing = isEmpty(defaultManageLocalData);

    if (
      userSelectedLanguage === LANGUAGE_CODE.EN_US &&
      isDefaultDataMissing &&
      isEnGbMissing
    ) {
      setValue(ManageLocaleKey.englishUsName, englishName);
      setValue(ManageLocaleKey.englishUkName, englishName);
    } else if (
      userSelectedLanguage === LANGUAGE_CODE.EN_GB &&
      isDefaultDataMissing &&
      isEnUsMissing
    ) {
      setValue(ManageLocaleKey.englishUkName, englishName);
      setValue(ManageLocaleKey.englishUsName, englishName);
    } else if (
      userSelectedLanguage !== LANGUAGE_CODE.EN_GB &&
      userSelectedLanguage !== LANGUAGE_CODE.EN_US
    ) {
      if (isEnUsMissing && !defaultManageLocalData[LANGUAGE_CODE.EN_US]) {
        setValue(ManageLocaleKey.englishUsName, englishName);
      }
      if (isEnGbMissing && !defaultManageLocalData[LANGUAGE_CODE.EN_GB]) {
        setValue(ManageLocaleKey.englishUkName, englishName);
      }
    }
  }, []);

  const isLanguageMissing = (
    localizedNames: LocalizedProgramTemplateNames[],
    lang: string,
  ): boolean => isEmpty(localizedNames.find((item) => item.localekey === lang));

  useEffect(() => {
    setIsManageLocalChange(isDirty);
  }, [isDirty]);

  const onHandleCloseModal = () => {
    if (isDirty) {
      setShowRemoveModal(true);
    } else {
      handleCloseModal();
    }
  };

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={fetchComponentTitle(createProgramSiteCoreKeys.manageLocale)}
      primaryButtonTitle={
        isEditTemplate ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={() => handleSubmit(onConfirmClick)()}
      onSecondaryButtonClick={onHandleCloseModal}
      className="manage-local-modal"
    >
      <ManageLocaleWrapper>
        <form data-testid="manage-locale">
          <MuiGrid container>
            {mergedLanguages.map(
              (
                { key, placeholderKey, label, isMandatory, isEnglish }: any, //any with type
              ) => (
                <MuiGrid
                  key={key}
                  size={gridSize}
                  className={gridSizeMediumClass}
                >
                  <FormController control={control} name={key}>
                    <MuiTextField
                      label={label}
                      placeholder={placeholderKey}
                      id={`${pageId}-${key}`}
                      isMandatory={isMandatory || isEnglish}
                      maxLength={MAX_LENGTH_75}
                      showCloseIcon
                      requiredARIALabel={commonARIALabel.required}
                      tooltipMessage={
                        commonValidationMessages.fieldSpecialCharactersTooltipMessage
                      }
                      tooltipHeaderTitle={
                        commonValidationMessages.allowedSpecialCharactersTitle
                      }
                    />
                  </FormController>
                </MuiGrid>
              ),
            )}
          </MuiGrid>
        </form>
        {showRemoveModal && (
          <CancelFieldModal
            showModal={showRemoveModal}
            handleModalClose={() => setShowRemoveModal(false)}
            handlePrimaryButtonClick={handleCloseModal}
          />
        )}
      </ManageLocaleWrapper>
    </MuiModal>
  );
};
